// Rhythm pills, guide toggle, shared-vs-each team mode, and per-member progress.
import { useState } from "react";
import { toast } from "sonner";
import { BookOpen, Check, Hand, Repeat, Users } from "lucide-react";
import { RHYTHM, doneThisPeriod, tickTask } from "../_shared";

export function RhythmPicker({ task, onChange }) {
  return (
    <div className="space-y-2" data-testid="task-drawer-rhythm">
      <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1"><Repeat className="w-3 h-3" /> How often</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {RHYTHM.map((r) => {
          const on = (task.recurrence_class || "none") === r.key;
          return (
            <button key={r.key} type="button" onClick={() => onChange({ recurrence_class: r.key })} data-testid={`task-rhythm-${r.key}`}
              className={`px-3 py-1 rounded-full border text-[12px] transition-colors ${on ? r.cls : "border-white/10 text-slate-400 hover:text-white"}`}>{r.label}</button>
          );
        })}
        <button type="button" onClick={() => onChange({ guide: !task.guide })} data-testid="task-guide-toggle"
          title="Guides are standing instructions (e.g. 'Answer phones') — no tick box"
          className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[12px] transition-colors ${task.guide ? "bg-amber-500/15 text-amber-200 border-amber-500/40" : "border-white/10 text-slate-400 hover:text-white"}`}>
          <BookOpen className="w-3.5 h-3.5" /> {task.guide ? "Guide (no tick box)" : "Make it a guide"}
        </button>
      </div>
    </div>
  );
}

export function TeamModePicker({ task, onChange }) {
  const opts = [
    ["shared", Hand, "Shared", "One task — anyone in the team claims or completes it"],
    ["each", Users, "Each member", "Everyone in the team ticks it off for themselves"],
  ];
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="task-drawer-team-mode">
      {opts.map(([k, Icon, label, sub]) => {
        const on = (task.assign_mode || "shared") === k;
        return (
          <button key={k} type="button" onClick={() => onChange({ assign_mode: k })} data-testid={`task-team-mode-${k}`}
            className={`text-left rounded-lg border px-3 py-2 transition-colors ${on ? "border-teal-400/50 bg-teal-500/10" : "border-white/10 hover:border-white/20"}`}>
            <div className={`text-[12.5px] font-medium flex items-center gap-1.5 ${on ? "text-teal-200" : "text-slate-200"}`}><Icon className="w-3.5 h-3.5" /> {label}</div>
            <div className="text-[11px] text-slate-500 leading-snug">{sub}</div>
          </button>
        );
      })}
    </div>
  );
}

export function EachMemberProgress({ task, team, users = [], meKey, onTicked }) {
  const [busy, setBusy] = useState(false);
  const members = team?.member_ids || [];
  const byId = Object.fromEntries(users.map((u) => [u.id, u]));
  const done = (uid) => doneThisPeriod(task.done_by?.[uid], task.recurrence_class);
  const mine = done(meKey);
  const toggle = async () => {
    setBusy(true);
    try { const r = await tickTask(task.id, !mine); onTicked?.(r.task); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); } finally { setBusy(false); }
  };
  if (task.guide) return null;
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.015] p-3" data-testid="task-drawer-each-progress">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono flex-1">
          Team progress · {members.filter(done).length}/{members.length} {task.recurrence_class === "daily" ? "today" : task.recurrence_class === "weekly" ? "this week" : task.recurrence_class === "monthly" ? "this month" : ""}
        </div>
        {members.includes(meKey) && (
          <button type="button" onClick={toggle} disabled={busy} data-testid="task-drawer-tick-mine"
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border ${mine ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200" : "border-white/15 text-slate-200 hover:bg-white/5"}`}>
            <Check className="w-3 h-3" /> {mine ? "Done by me" : "Mark mine done"}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {members.map((uid) => (
          <span key={uid} className={`px-2 py-0.5 rounded-full text-[11px] border ${done(uid) ? "border-emerald-400/40 text-emerald-200 bg-emerald-500/10" : "border-white/10 text-slate-400"}`} data-testid={`task-each-member-${uid}`}>
            {done(uid) && "✓ "}{byId[uid]?.name || uid.slice(0, 8)}
          </span>
        ))}
      </div>
    </div>
  );
}
